import { CONSENT_VERSIONS, DISCLAIMERS } from '@erectus/shared';

/**
 * The policies (docs/LEGAL_PRIVACY.md §2), in force as written since
 * 2026-10-07. A digital-health attorney review is planned once usage grows or
 * paid marketing starts (docs/RELEASE.md); update CONSENT_VERSIONS when a
 * reviewed version replaces these. Two rules govern the wording:
 *  - FDA General Wellness scope: no diagnosis, treatment or cure claims (§4).
 *  - The words "research"/"study" are never used for data collection (§0).
 */
export interface LegalSection {
  heading: string;
  body: string[];
}

export interface LegalDoc {
  slug: string;
  title: string;
  updated: string;
  intro: string;
  sections: LegalSection[];
}

const CONTACT = 'hello@uhzlab.com';
const REGION = 'United States (Google Cloud us-central1)';

export const LEGAL_CONTENT: LegalDoc[] = [
  {
    slug: 'privacy-policy',
    title: 'Privacy Policy',
    updated: CONSENT_VERSIONS.privacy,
    intro:
      'Erectus measures sitting posture with your phone camera. The analysis happens on your device. This policy explains the small amount of data that does leave it.',
    sections: [
      {
        heading: 'What we collect',
        body: [
          'Account: email address and a user ID from your sign-in provider.',
          'Session summaries: posture angles, scores, episode timings and session metadata (length, frame rate, model version).',
          'Your settings: goals, sitting-hours bucket, equipment, work hours and reminder schedule, and the program you pick.',
          'If you choose to enter them: sex, height and weight.',
          'Habit logs: when you log a rest break or finish an exercise snack, and which one.',
          'Feedback: your thumbs up/down, the stretches you mark as wrong and the reason you choose.',
          'Product analytics: screen and feature events in bucketed form. No angle, score or health value is ever sent to analytics. Turn it off any time in Settings › Your data › Usage statistics; a browser sending Global Privacy Control starts with it off.',
          'Diagnostics: crash reports, not linked to your identity.',
        ],
      },
      {
        heading: 'What we never collect',
        body: [
          'Video or still images. Camera frames are analyzed in memory on your device and discarded immediately. They are not stored, uploaded or converted to files.',
          'Location, contacts or advertising identifiers.',
          'Any symptom, diagnosis, pain or condition information — the app has no field for it.',
        ],
      },
      {
        heading: 'Keypoint data (optional)',
        body: [
          'If you are 18 or over you can separately opt in to share the numeric joint coordinates from your sessions so we can improve detection. We ask once when you sign up, with nothing pre-selected; it is described in its own notice, and you can change your answer at any time in Settings.',
        ],
      },
      {
        heading: 'How we use it',
        body: [
          'To run the product: score your sessions, show your history, generate coaching text and send the reminders you asked for.',
          'To improve detection accuracy, using the feedback you give and — only with your separate opt-in — shared keypoint data.',
          'We do not sell personal data and we do not use health-related data for advertising.',
        ],
      },
      {
        heading: 'Who else processes it',
        body: [
          'Google Firebase (authentication, database, storage, hosting, analytics, crash reporting) as our infrastructure provider.',
          'Google Gemini, through our own server, to generate coaching text. What is sent is the session numbers and your settings — never your identity or raw keypoints.',
        ],
      },
      {
        heading: 'Where it is stored and for how long',
        body: [
          `Data is stored in ${REGION}.`,
          'Session summaries are kept until you delete them or delete your account.',
          'Shared keypoint files are kept until you withdraw sharing or delete your account.',
          'Server logs are kept for 90 days.',
        ],
      },
      {
        heading: 'Your choices',
        body: [
          'Delete your account and all data from Settings at any time; deletion covers the database, file storage and your sign-in.',
          'Export or ask questions by emailing ' + CONTACT + '.',
          'We honour Global Privacy Control signals on our website.',
        ],
      },
      {
        heading: 'Children',
        body: ['Erectus is not for anyone under 13. Sign-up is blocked by a birth-year gate.'],
      },
      { heading: 'Contact', body: [CONTACT] },
    ],
  },
  {
    slug: 'consumer-health-data',
    title: 'Consumer Health Data Privacy Policy',
    updated: CONSENT_VERSIONS.chdp,
    intro:
      'This notice is provided in the form required by the Washington My Health My Data Act and similar state laws (Nevada SB 370, Connecticut). It applies to all users, not just Washington residents.',
    sections: [
      {
        heading: 'Consumer health data we collect',
        body: [
          'Bodily measurements derived from camera analysis: neck (ear–shoulder) angle, trunk lean, shoulder protraction, hip, knee, elbow and wrist angles, and the posture state derived from them.',
          'Session-level aggregates: posture score, good-posture share, episode counts and timings.',
          'Only if you enter them: height and weight. Logs of rest breaks and exercise snacks you mark as done.',
          'Optional, with separate 18+ consent: the numeric joint coordinate time series for a session.',
          'We do not collect conditions, diagnoses, symptoms, pain reports, medications, treatments, precise location or biometric identifiers used to identify you.',
        ],
      },
      {
        heading: 'Sources',
        body: [
          'Directly from you: the on-device analysis of your camera feed, and the settings and feedback you enter.',
        ],
      },
      {
        heading: 'Purposes',
        body: [
          'Providing the product you asked for: scoring a session, showing your history, generating general posture and mobility suggestions, and sending reminders.',
          'Improving detection accuracy.',
          'We do not use consumer health data for advertising, and we do not use it to infer any condition.',
        ],
      },
      {
        heading: 'Sharing',
        body: [
          'With our infrastructure provider (Google Firebase) as a processor.',
          'With our coaching model provider (Google Gemini) in aggregate, non-identifying form, through our server.',
          'We do not sell consumer health data. We have never sold it. Selling it would require your separate written authorization, which we do not seek.',
        ],
      },
      {
        heading: 'Your rights',
        body: [
          'Access: ask for a copy of your consumer health data.',
          'Deletion: delete it yourself in Settings, or ask us; we delete it from our systems and instruct our processors to do the same.',
          'Withdraw consent: turn off collection by deleting your account, and turn off keypoint sharing independently in Settings.',
          `To exercise any right, email ${CONTACT}. If we deny a request you may appeal by replying to our decision.`,
        ],
      },
      {
        heading: 'Geofencing',
        body: [
          'We do not use geofencing around any health care facility, and we do not collect location data at all.',
        ],
      },
    ],
  },
  {
    slug: 'terms',
    title: 'Terms of Service',
    updated: CONSENT_VERSIONS.tos,
    intro: 'These terms govern your use of Erectus.',
    sections: [
      {
        heading: 'Wellness use only',
        body: [DISCLAIMERS.long],
      },
      {
        heading: 'Eligibility',
        body: [
          'You must be at least 13 to use Erectus. Sharing keypoint data to improve detection requires you to be at least 18.',
        ],
      },
      {
        heading: 'Your account',
        body: [
          'Keep your sign-in secure. You are responsible for activity under your account.',
          'You can delete your account, and all data with it, from Settings.',
        ],
      },
      {
        heading: 'Acceptable use',
        body: [
          'Do not record other people without their knowledge. Erectus is for measuring your own posture.',
          'Do not attempt to extract our models, abuse the coaching service, or use the app to provide medical services.',
        ],
      },
      {
        heading: 'Availability and changes',
        body: [
          'The service is provided as is. Features, thresholds and scoring may change as detection improves; the version used for each session is recorded with it.',
        ],
      },
      {
        heading: 'Liability',
        body: [
          'To the maximum extent permitted by law, we are not liable for indirect or consequential damages. Nothing here limits liability that cannot be limited by law.',
        ],
      },
      {
        heading: 'Disputes',
        body: [
          'These terms are governed by the laws of the State of Delaware, without regard to conflict-of-law rules.',
        ],
      },
    ],
  },
  {
    slug: 'medical-disclaimer',
    title: 'Medical Disclaimer',
    updated: CONSENT_VERSIONS.tos,
    intro: DISCLAIMERS.long,
    sections: [
      {
        heading: 'Not a medical device',
        body: [
          'Erectus is a general wellness product. It does not diagnose, treat, prevent or mitigate any condition, and it is not a substitute for care from a qualified clinician.',
        ],
      },
      {
        heading: 'Measurement accuracy',
        body: [
          'Angles are estimated from a single camera view and carry roughly ±5° of error. Changes smaller than that are reported as unchanged. Values depend heavily on camera placement, clothing and lighting.',
        ],
      },
      {
        heading: 'Exercises',
        body: [
          'Exercise suggestions are general information commonly used for desk posture and mobility. They are not personalised medical instructions. Stop if you feel pain, and talk to a physical therapist or doctor about anything persistent.',
        ],
      },
    ],
  },
  {
    slug: 'detection-improvement',
    title: 'Detection-Improvement Data Notice',
    updated: CONSENT_VERSIONS.dataOptIn,
    intro:
      'This notice covers the optional sharing of numeric joint coordinates so we can make posture detection more accurate. Nothing is shared unless you choose Yes — we ask once when you sign up, with neither answer pre-selected — and it is limited to people aged 18 and over.',
    sections: [
      {
        heading: 'What is shared',
        body: [
          'A time series of joint coordinates for the session (normalized numbers, sampled a few times a second), the posture state we assigned to each moment, and any stretches you marked as wrong together with the reason you selected.',
          'Which sessions: every session that ends after you turn sharing on, and — at the moment you turn it on — the sessions still on your phone, which are those from the last 7 days.',
        ],
      },
      {
        heading: 'Your own access',
        body: [
          'Shared sessions are kept in your account, so you can replay them in the app on any device you sign in to. Without sharing, your phone keeps each session\'s replay for 7 days. This is a consequence of where the files are kept; we use shared data only to improve detection.',
        ],
      },
      {
        heading: 'What is not shared',
        body: [
          'Video and images. They are processed on your device and discarded; there is no code path that uploads a frame.',
          'Your name, email or user ID. Shared files are labelled with a separate scrambled identifier held apart from your account.',
        ],
      },
      {
        heading: 'How long it is kept',
        body: [
          'Until you withdraw sharing or delete your account. Either one deletes your shared keypoint files and removes your rows from every training export already made, straight away. A model already trained on that data cannot have a single person’s contribution taken back out of it, which is why we keep the shared fields minimal.',
        ],
      },
      {
        heading: 'How to withdraw',
        body: ['Settings → Your data → Manage sharing, one tap. No explanation needed. What you shared is deleted when you withdraw.'],
      },
      {
        heading: 'Consent record',
        body: [
          `We store your answer, the version of this notice it applies to (${CONSENT_VERSIONS.dataOptIn}) and the time you gave it.`,
        ],
      },
    ],
  },
  {
    slug: 'data-retention',
    title: 'Data Retention and Deletion Policy',
    // On-device frames: one 7-day window for everyone (opted-in sessions were
    // kept on the phone indefinitely before). That data never reaches us, so it
    // does not re-open consent (CONSENT_VERSIONS.privacy stays); the date shows
    // when this page last changed.
    updated: '2026-10-07',
    intro: 'How long each kind of data lives, and what deletion actually removes.',
    sections: [
      {
        heading: 'Retention periods',
        body: [
          'Session summaries: until you delete them or delete your account.',
          'On-device frame data (the joint positions behind each session\'s replay): kept on your phone for 7 days after each session, then deleted automatically. You can delete it sooner — one session from its report, every replay at once in Settings → Your data, or everything with your account. It reaches our servers only if you opted in to sharing; a session whose shared upload has not gone through yet stays on the phone until it does.',
          'Shared keypoint files: until you withdraw sharing or delete your account.',
          'Coaching text: stored with the session it belongs to, deleted with it.',
          'Server logs: 90 days.',
        ],
      },
      {
        heading: 'What deletion covers',
        body: [
          'Deleting your account removes your user document and subcollections, every session document and its feedback, every coaching record, every file under your storage folder, your rows in any training export, and your authentication record. The app also clears what it kept on your device.',
          'Deletion completes within 30 days; in practice it is immediate, and we log completion.',
        ],
      },
      {
        heading: 'Breach notification',
        body: [
          'As a health app outside HIPAA we are subject to the FTC Health Breach Notification Rule. If unsecured health data is breached or disclosed without authorization we notify affected users and the FTC within 60 days, and the media where the rule requires it.',
        ],
      },
    ],
  },
  {
    slug: 'cookies',
    title: 'Cookie and Tracking Notice',
    updated: CONSENT_VERSIONS.privacy,
    intro: 'What the website and the app store on your device.',
    sections: [
      {
        heading: 'Strictly necessary',
        body: [
          'Sign-in tokens and your local session cache (IndexedDB). Without these the app cannot keep you signed in or build a report.',
        ],
      },
      {
        heading: 'Analytics',
        body: [
          'Google Analytics for Firebase, used only after you allow it on the consent banner. Events are bucketed and contain no health values. Advertising identifier collection is disabled.',
        ],
      },
      {
        heading: 'Your control',
        body: [
          'Decline analytics on the banner, or clear site data at any time. We honour Global Privacy Control.',
        ],
      },
    ],
  },
  {
    slug: 'security',
    title: 'Security Overview',
    updated: CONSENT_VERSIONS.privacy,
    intro: 'A one-page summary of how the service is protected.',
    sections: [
      {
        heading: 'Architecture',
        body: [
          'Posture analysis runs entirely on your device. The server never receives camera data.',
          `Data is stored in ${REGION}. Transport is TLS; data at rest is encrypted by the platform.`,
        ],
      },
      {
        heading: 'Access control',
        body: [
          'Database and storage rules allow a user to read and write only their own documents and files. Coaching records are written by the server only.',
          'App Check (reCAPTCHA Enterprise on the web, App Attest and Play Integrity on mobile) is enforced on callable functions and storage.',
          'API keys for model providers live in Secret Manager and are never shipped to the client.',
        ],
      },
      {
        heading: 'Data minimisation',
        body: [
          'No video, no location, no advertising identifiers. Analytics carries no health values.',
          'Exported training data is pseudonymised and stored in a separate project with least-privilege access and audit logging.',
        ],
      },
      {
        heading: 'Incident response',
        body: [
          `A named owner triages any suspected incident, scopes it using platform audit logs, and notifies as required within 60 days. Report a concern to ${CONTACT}.`,
        ],
      },
    ],
  },
  {
    slug: 'store-privacy-labels',
    title: 'App Store Privacy Label / Play Data Safety (draft)',
    updated: CONSENT_VERSIONS.privacy,
    intro:
      'Published for transparency so our store declarations and this policy can be checked against each other.',
    sections: [
      {
        heading: 'Collected and linked to you',
        body: [
          'Contact info: email address — app functionality.',
          'Health and fitness: session and posture-check summaries (angles, scores, times) — app functionality and personalisation.',
          'User content: questions you ask Darwin — app functionality.',
          'Identifiers: user ID — app functionality.',
          'Diagnostics: crash reports and capture performance (frames per second, phone tilt) — app functionality.',
        ],
      },
      {
        heading: 'Collected, not linked to you',
        body: [
          'Usage data: which screens and features are used — analytics.',
          'Identifiers: an analytics app-instance ID (not an advertising ID) — analytics.',
          'Approximate location: country and region derived by Google Analytics from the network address — analytics. No precise location.',
        ],
      },
      {
        heading: 'Not collected',
        body: [
          'Photos or video: the camera is used for real-time on-device analysis only; nothing is stored or transmitted.',
          'Precise location, contacts, browsing history, advertising identifiers.',
        ],
      },
      {
        heading: 'Tracking',
        body: ['None. We do not track users across apps or websites.'],
      },
      {
        heading: 'Play declarations',
        body: [
          'Health apps declaration: activity and fitness tracking only. No medical functionality declared. Health Connect is not used.',
          'Camera permission purpose: "on-device posture analysis; no video stored or transmitted".',
          'Account deletion is available in the app and via a web request.',
        ],
      },
    ],
  },
  {
    // Support URL for both store listings.
    slug: 'support',
    title: 'Support',
    updated: CONSENT_VERSIONS.privacy,
    intro: `Questions, problems or feedback: email ${CONTACT}. We usually reply within two working days.`,
    sections: [
      {
        heading: 'Getting a good measurement',
        body: [
          'Stand the phone at your side, roughly 5–8 ft (1.5–2.5 m) away, so your head to your knees are in frame. Propping it against something is fine.',
          'Keep your ear visible, wear fitted clothing, and have light in front of you rather than behind.',
          'The checklist on the camera screen shows what is missing; the session starts once every check passes.',
        ],
      },
      {
        heading: 'Reminders do not arrive',
        body: [
          'Allow notifications for Erectus in your phone settings, then check Settings › Reminders in the app. Reminders are sent on the hour you choose, in your time zone.',
        ],
      },
      {
        heading: 'Your data',
        body: [
          'Video never leaves your phone. Delete a single session from its report, or your account and all its data from Settings › Account.',
          `To ask for a copy or deletion by email, write to ${CONTACT}.`,
        ],
      },
      {
        heading: 'Contact',
        body: [CONTACT],
      },
    ],
  },
];
